import ResultOfferCard from './ResultOfferCard';
import { extractIataCode } from '../../utils/airportCodes';
import { formatResultDate, formatTime12h, locationCode, stopsLabel } from '../../utils/resultDisplay';
import { SITE_NAME } from '../../constants/site';

export default function FlightResultCard({ item, searchParams }) {
  const fromCode = item.fromCode || locationCode(item.from, extractIataCode(item.from));
  const toCode = item.toCode || locationCode(item.to, extractIataCode(item.to));
  const tripType = searchParams?.get('trip') || 'roundtrip';
  const isRoundTrip =
    tripType !== 'oneway' && (item.isRoundTrip || Boolean(item.returnDepart));
  const dateLabel = formatResultDate(item.departAt || searchParams?.get('depart'));
  const returnDateLabel = formatResultDate(
    item.returnDepartAt || searchParams?.get('return') || ''
  );

  const bookingLines = [
    { label: 'Carrier type', value: item.airline },
    { label: 'Trip', value: isRoundTrip ? 'Round trip' : 'One way' },
    { label: 'Outbound', value: `${item.from} (${fromCode}) → ${item.to} (${toCode})` },
    { label: 'Outbound depart', value: item.depart },
    { label: 'Outbound arrive', value: item.arrive },
    { label: 'Outbound duration', value: item.duration },
    { label: 'Outbound stops', value: stopsLabel(item.stops) },
  ];

  if (isRoundTrip) {
    bookingLines.push(
      { label: 'Return', value: `${item.to} (${toCode}) → ${item.from} (${fromCode})` },
      { label: 'Return depart', value: item.returnDepart },
      { label: 'Return arrive', value: item.returnArrive },
      { label: 'Return duration', value: item.returnDuration },
      { label: 'Return stops', value: stopsLabel(item.returnStops ?? 0) }
    );
  }

  bookingLines.push(
    { label: 'Class', value: item.class },
    { label: 'Flight', value: item.flightNumber },
    { label: 'Total price', value: `$${Number(item.price).toFixed(2)}` }
  );

  const detailRows = [
    { label: 'Trip', value: isRoundTrip ? 'Round trip' : 'One way' },
    { label: 'Outbound', value: `${fromCode} ${formatTime12h(item.depart)} → ${toCode} ${formatTime12h(item.arrive)}` },
  ];
  if (isRoundTrip) {
    detailRows.push({
      label: 'Return',
      value: `${toCode} ${formatTime12h(item.returnDepart)} → ${fromCode} ${formatTime12h(item.returnArrive)}`,
    });
  }
  detailRows.push(
    { label: 'Class', value: item.class },
    { label: 'Flight', value: item.flightNumber },
    { label: 'Total', value: `$${Number(item.price).toFixed(2)}` }
  );

  return (
    <ResultOfferCard
      dateLabel={isRoundTrip ? `${dateLabel} · Return ${returnDateLabel}` : dateLabel}
      providerName={item.airline}
      providerLogo={null}
      providerFallback="✈"
      outboundLabel="Departing"
      routeLeft={{ code: fromCode, time: formatTime12h(item.depart) }}
      routeRight={{ code: toCode, time: formatTime12h(item.arrive) }}
      routeDuration={item.duration}
      routeStops={stopsLabel(item.stops)}
      returnRoute={
        isRoundTrip
          ? {
              label: 'Returning',
              left: { code: toCode, time: formatTime12h(item.returnDepart) },
              right: { code: fromCode, time: formatTime12h(item.returnArrive) },
              duration: item.returnDuration,
              stops: stopsLabel(item.returnStops ?? 0),
            }
          : null
      }
      price={{ amount: item.perPerson ?? item.price, currency: item.currency || 'USD' }}
      detailsTitle="Flight Details"
      detailRows={detailRows}
      bookNowProps={{
        serviceType: 'flight',
        bookingPayload: {
          subject: `${SITE_NAME} — Flight booking`,
          bookingLines,
          offer: {
            providerName: item.airline,
            fromCode,
            toCode,
            fromTime: formatTime12h(item.depart),
            toTime: formatTime12h(item.arrive),
            dateLabel,
            duration: item.duration,
            stops: stopsLabel(item.stops),
            class: item.class,
            isRoundTrip,
            returnFromCode: toCode,
            returnToCode: fromCode,
            returnFromTime: formatTime12h(item.returnDepart),
            returnToTime: formatTime12h(item.returnArrive),
            returnDateLabel,
            returnDuration: item.returnDuration,
            returnStops: stopsLabel(item.returnStops ?? 0),
          },
          price: {
            base: item.perPerson ?? item.price,
            total: item.price,
            travelerLabel: '1 Traveler(s) - Adult',
          },
        },
      }}
    />
  );
}
